// Pure helpers for an R18 recommendation row: what state word it wears, which buttons it gets,
// and how its re-check reads in a sentence.
//
// Two things this file exists to make structural rather than careful:
//
//  1. **A zero is never dressed up as a measurement.** `verifySummary` on `checked: 0` says what
//     did not happen and why; it never produces "no effect", because nothing was measured.
//  2. **The paywall changes a button, not a status.** `upgradeNotice` reads the 402 body and
//     returns something to SAY; no caller is handed a new status, because the back end did not
//     move the row and neither may the screen.

export const REC_STATUSES = ["proposed", "planned", "in_progress", "verifying", "passed", "failed", "dismissed"];

/** Live = still owed something. `verifying` is live but no longer the client's move. */
export const LIVE_STATUSES = ["proposed", "planned", "in_progress", "verifying", "failed"];

export const STATUS_LABELS = {
  proposed: "Suggested",
  planned: "On the plan",
  in_progress: "Maya is on it",
  verifying: "Checking the numbers",
  passed: "Done",
  failed: "Did not land",
  dismissed: "Skipped",
};

export const STATUS_TONES = {
  proposed: "open",
  planned: "open",
  in_progress: "working",
  verifying: "working",
  passed: "done",
  failed: "bad",
  dismissed: "muted",
};

/** Why somebody skips a row. Chips, so nobody has to type to say no. */
export const DISMISS_REASONS = ["Not relevant to us", "Already handled", "Not right now", "Too much work"];

export function status(row) {
  return String(row?.status || "proposed");
}

export function isLive(row) {
  return LIVE_STATUSES.includes(status(row));
}

export function statusLabel(row) {
  const current = status(row);
  // "Maya is on it" on a row the CLIENT owns is a sentence about the wrong person. The state is
  // the same; who is standing in it is not.
  if (current === "in_progress" && !isMayaRow(row)) return "You started it";
  return STATUS_LABELS[current] || STATUS_LABELS.proposed;
}

export function statusTone(row) {
  return STATUS_TONES[status(row)] || "open";
}

export function isMayaRow(row) {
  return String(row?.who || "client") === "maya";
}

/**
 * The buttons this row gets, in the order they are drawn.
 *
 * `start` only exists where there is something for Maya to run; `done` is the client's word and
 * closes the row outright (Rocs 2026-09-13: a client who says they did it is believed, not
 * re-checked); `dismiss` is always available while the row is live, because refusing a
 * recommendation must never require doing it first.
 */
export function actionsFor(row) {
  const current = status(row);
  if (["passed", "dismissed"].includes(current)) return [];
  const actions = [];
  if (["proposed", "planned", "failed"].includes(current) && isMayaRow(row)) {
    actions.push({ id: "start", label: current === "failed" ? "Try again" : "Let Maya do it", primary: true });
  }
  if (["proposed", "planned", "failed"].includes(current) || (current === "in_progress" && !isMayaRow(row))) {
    actions.push({ id: "done", label: "I did it", primary: !isMayaRow(row) });
  }
  actions.push({ id: "skip", label: "Skip", primary: false });
  return actions;
}

/** The one sentence a re-check gets. Never "no effect" when nothing was measured. */
export function verifySummary(row) {
  const result = row?.verify_result;
  if (!result) return "";
  const checked = Number(result.checked || 0);
  if (checked > 0) {
    const passed = Number(result.passed || 0);
    const failed = Number(result.failed || 0);
    const noun = checked === 1 ? "question" : "questions";
    return `Re-checked ${checked} ${noun} · ${passed} improved, ${failed} unchanged.`;
  }
  const reason = String(result.reason || "");
  if (reason === "client_reported") return "Closed on your word — nothing was re-measured.";
  if (reason) return `Nothing re-measured yet — ${reason.replaceAll("_", " ")}.`;
  return "Nothing re-measured yet.";
}

export function expectedLift(row) {
  return String(row?.expected_lift?.text || "").trim();
}

export function evidenceLines(row) {
  return (row?.evidence || []).map((item) => String(item?.text || "").trim()).filter(Boolean);
}

/**
 * What a 402 from `start` means on screen: a sentence, and the fact that the row has not moved.
 * Anything that is not a 402 is handed back as an ordinary error for the caller to show.
 */
export function upgradeNotice(error) {
  if (!error || error.status !== 402) return null;
  const payload = error.payload || {};
  return {
    message: String(payload.message || error.message || "Running this for you is an Autopilot action."),
    planSaved: payload.plan_saved !== false,
    need: String(payload.need || "autopilot"),
  };
}

/** The rows the client is shown, and the one that is emphasised (`R18_ONE_AT_A_TIME`). */
export function visibleRows(rows) {
  return (rows || []).filter((row) => !row.withheld && status(row) !== "dismissed");
}

export function skippedRows(rows) {
  return (rows || []).filter((row) => status(row) === "dismissed");
}

export function topRowId(rows) {
  const live = visibleRows(rows).filter((row) => ["proposed", "planned"].includes(status(row)));
  return live.length ? live[0].id : null;
}
